import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import {
  AccountTree as SystemIcon,
  Article as ArticleIcon,
  AutoAwesome as SparkIcon,
  Cached as CacheIcon,
  CheckCircle as CheckIcon,
  Input as InputIcon,
  OpenInNew as OpenIcon,
  Output as OutputIcon,
  Psychology as ExpertIcon,
  SmartToy as AgentIcon,
  TrendingUp as TradingIcon,
  WarningAmber as RiskIcon,
} from '@mui/icons-material';
import {
  AI_SYSTEM_HELP_SECTIONS,
  TUTORIAL_MODULES,
  TUTORIAL_RESOURCES,
} from '../../../tutorial/tutorialContent';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel({ children, value, index }: TabPanelProps) {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ py: 2 }}>{children}</Box>}
    </div>
  );
}

const conceptCards = [
  {
    icon: <SparkIcon />,
    title: 'LLM',
    body: 'A model that turns instructions and context into useful text or structured output. It needs explicit data, boundaries, and schema.',
  },
  {
    icon: <SystemIcon />,
    title: 'AI System',
    body: 'A repeatable decision workflow: typed inputs, experts, schema, validation, schedules, runs, storage, and audit history.',
  },
  {
    icon: <ExpertIcon />,
    title: 'Expert',
    body: 'A specialized reasoning or deterministic unit inside a system. Prompt experts interpret; code experts calculate and enforce rules.',
  },
  {
    icon: <AgentIcon />,
    title: 'Agent',
    body: 'An autonomous operator with a goal, tools, memory, validators, and a loop for multi-step investigation or assistance.',
  },
];

const fieldExamples = [
  {
    icon: <InputIcon />,
    title: 'Input Types',
    body: 'Use price data for candles, indicator inputs for computed signals, storage inputs for durable context, and prompt snippets for reusable instruction blocks.',
  },
  {
    icon: <OutputIcon />,
    title: 'Output Schema',
    body: 'Prefer fields such as decision, confidence, setup_type, invalidation_level, risk_notes, evidence, and action_allowed.',
  },
  {
    icon: <CacheIcon />,
    title: 'Caching',
    body: 'Cache stable analysis or policy interpretation. Do not cache live prices, account state, or execution-critical context without freshness checks.',
  },
  {
    icon: <TradingIcon />,
    title: 'Trading Fit',
    body: 'Classify the strategy first: trend, mean reversion, breakout, event-driven, portfolio, or risk workflow. Each needs different validation.',
  },
];

const HelpDocumentationPanel: React.FC = () => {
  const [tab, setTab] = useState(0);
  const activeSection = AI_SYSTEM_HELP_SECTIONS[tab];
  const matchingModule = useMemo(
    () =>
      TUTORIAL_MODULES.find((module) =>
        module.title.toLowerCase().includes(activeSection.title.toLowerCase().split(',')[0])
      ),
    [activeSection.title]
  );

  return (
    <Box>
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 2,
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
        }}
      >
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} justifyContent="space-between">
          <Box sx={{ maxWidth: 860 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <SystemIcon color="primary" />
              <Typography variant="overline" color="primary" fontWeight={700}>
                AI System Help
              </Typography>
            </Stack>
            <Typography variant="h5" fontWeight={800} gutterBottom>
              Build AI systems as auditable trading workflows
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8 }}>
              This help screen now mirrors the full Valpha tutorial: LLM foundations, expert design, typed inputs,
              output schemas, caching, agents, trading strategy patterns, validation, and operations. It uses the same
              course assets and service-instruction keys that agents can retrieve when helping a user.
            </Typography>
          </Box>

          <Stack spacing={1.25} sx={{ minWidth: { md: 260 } }}>
            <Button component="a" href="/tutorial" variant="contained" endIcon={<OpenIcon />}>
              Open Full Tutorial
            </Button>
            <Button
              component="a"
              href="/tutorial/valpha-ai-system-agent-trading-tutorial.pdf"
              target="_blank"
              rel="noreferrer"
              variant="outlined"
              endIcon={<ArticleIcon />}
            >
              AI Course PDF
            </Button>
          </Stack>
        </Stack>
      </Paper>

      <Alert severity="info" icon={<AgentIcon />} sx={{ mb: 2 }}>
        Agents can reuse this curriculum through service instructions. Ask an agent to search for the tutorial service
        instructions, then fetch a module by service key for grounded help.
      </Alert>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        {conceptCards.map((card) => (
          <Grid size={{ xs: 12, md: 6, lg: 3 }} key={card.title}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent>
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
                  <Box sx={{ color: 'primary.main', display: 'flex' }}>{card.icon}</Box>
                  <Typography variant="subtitle1" fontWeight={700}>
                    {card.title}
                  </Typography>
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                  {card.body}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Paper variant="outlined" sx={{ mb: 2 }}>
        <Tabs
          value={tab}
          onChange={(_, nextTab) => setTab(nextTab)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          {AI_SYSTEM_HELP_SECTIONS.map((section) => (
            <Tab key={section.id} label={section.title} />
          ))}
        </Tabs>

        {AI_SYSTEM_HELP_SECTIONS.map((section, index) => (
          <TabPanel key={section.id} value={tab} index={index}>
            <Box sx={{ px: 2, pb: 2 }}>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, lg: 7 }}>
                  <Typography variant="h6" fontWeight={800} gutterBottom>
                    {section.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8, mb: 2 }}>
                    {section.summary}
                  </Typography>

                  <Grid container spacing={1.5}>
                    {section.lessons.map((lesson) => (
                      <Grid size={{ xs: 12, md: 4 }} key={lesson.title}>
                        <Card variant="outlined" sx={{ height: '100%' }}>
                          <CardContent>
                            <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                              {lesson.title}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                              {lesson.body}
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>

                  <Divider sx={{ my: 2 }} />

                  <Typography variant="subtitle2" fontWeight={800} gutterBottom>
                    Build checklist
                  </Typography>
                  <List dense disablePadding>
                    {section.checklist.map((item) => (
                      <ListItem key={item} disableGutters>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <CheckIcon color="success" fontSize="small" />
                        </ListItemIcon>
                        <ListItemText
                          primary={item}
                          primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Grid>

                <Grid size={{ xs: 12, lg: 5 }}>
                  <Box
                    component="figure"
                    sx={{
                      m: 0,
                      overflow: 'hidden',
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 2,
                      bgcolor: 'background.default',
                    }}
                  >
                    <Box
                      component="img"
                      src={section.image.src}
                      alt={section.image.alt}
                      sx={{ display: 'block', width: '100%', height: 'auto' }}
                    />
                    <Typography
                      component="figcaption"
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: 'block', p: 1.5, borderTop: '1px solid', borderColor: 'divider' }}
                    >
                      {section.image.caption}
                    </Typography>
                  </Box>

                  <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 2 }}>
                    {section.appLinks.map((link) => (
                      <Button key={link.href} component="a" href={link.href} size="small" variant="outlined">
                        {link.label}
                      </Button>
                    ))}
                    {matchingModule && (
                      <Button component="a" href={`/tutorial`} size="small" variant="contained">
                        Course module
                      </Button>
                    )}
                  </Stack>
                </Grid>
              </Grid>
            </Box>
          </TabPanel>
        ))}
      </Paper>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        {fieldExamples.map((example) => (
          <Grid size={{ xs: 12, md: 6 }} key={example.title}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent>
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
                  <Box sx={{ color: 'primary.main', display: 'flex' }}>{example.icon}</Box>
                  <Typography variant="subtitle1" fontWeight={700}>
                    {example.title}
                  </Typography>
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.75 }}>
                  {example.body}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
          <RiskIcon color="warning" />
          <Typography variant="subtitle1" fontWeight={800}>
            Trading automation rule
          </Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8, mb: 2 }}>
          AI can support analysis, explanation, classification, and structured decision writing. Live trading still
          needs deterministic checks for account, sizing, max loss, broker state, event risk, approvals, and available
          AI balance before any execution path is allowed.
        </Typography>

        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
          {TUTORIAL_RESOURCES.map((resource) => (
            <Chip
              key={resource.href}
              clickable
              component="a"
              href={resource.href}
              target="_blank"
              rel="noreferrer"
              label={resource.label}
              icon={<ArticleIcon />}
              variant="outlined"
            />
          ))}
          <Chip
            clickable
            component="a"
            href="/tutorial"
            label="Tutorial screen"
            color="primary"
            icon={<OpenIcon />}
          />
        </Stack>
      </Paper>
    </Box>
  );
};

export default HelpDocumentationPanel;
